import { call, put, takeLatest } from 'redux-saga/effects';
import type { PayloadAction } from '@reduxjs/toolkit';
import request from '@/utils/request';
import { auth } from '@/utils/auth';
import API from '@/config/api';
import logger from '@/utils/logger';
import {
  loginRequest,
  loginSuccess,
  loginFailure,
  googleLoginRequest,
  logoutRequest,
  logoutSuccess,
} from './authSlice';
import type { LoginResponse } from '@/types/auth.types';

function* handleLogin(action: PayloadAction<{ email: string; password: string; remember: boolean }>): Generator {
  try {
    const { email, password, remember } = action.payload;
    const response: any = yield call(request.post, API.LOGIN, { email, password });

    logger.log('Login API Response:', response);
    logger.log('Response data:', response.data);
    logger.log('Response data type:', typeof response.data);
    logger.log('Response data keys:', response.data ? Object.keys(response.data) : 'null');

    // Handle different API response structures
    // Some APIs return { data: { token, user } }
    // Others return { data: { data: { token, user } } }
    let responseData = response.data;
    if (responseData && responseData.data && !responseData.token) {
      logger.log('Detected nested data structure, unwrapping...');
      responseData = responseData.data;
    }

    const { token, user } = responseData;

    logger.log('Extracted token:', token);
    logger.log('Extracted user:', user);

    if (!token || !user) {
      throw new Error('Invalid response: missing token or user data');
    }

    auth.setToken(token, remember);
    auth.setUserInfo(user, remember);

    logger.log('Token and user saved to storage');

    // Verify token was saved before proceeding
    const savedToken = auth.getToken();
    if (!savedToken) {
      logger.error('Token was not saved to storage!');
      throw new Error('Failed to persist authentication token');
    }
    logger.log('Token verified in storage');

    logger.log('Dispatching loginSuccess...');

    yield put(loginSuccess({ user, token }));

    logger.log('loginSuccess dispatched successfully');
  } catch (error: unknown) {
    logger.error('Login error:', error);
    const message = error instanceof Error ? error.message : 'Login failed';
    yield put(loginFailure(message));
  }
}

function* handleGoogleLogin(action: PayloadAction<{ credential: string; remember: boolean }>) {
  try {
    const { credential, remember } = action.payload;
    const response: { data: LoginResponse } = yield call(request.post, `${API.LOGIN}/google`, { credential });
    const { token, user } = response.data;

    auth.setToken(token, remember);
    auth.setUserInfo(user, remember);

    yield put(loginSuccess({ user, token }));
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Google login failed';
    yield put(loginFailure(message));
  }
}

function* handleLogout() {
  try {
    yield call(request.post, API.LOGOUT);
  } catch {
    // Logout API failure is non-critical — clear local state regardless
  } finally {
    auth.clear();
    yield put(logoutSuccess());
  }
}

export default function* authSaga() {
  yield takeLatest(loginRequest.type, handleLogin);
  yield takeLatest(googleLoginRequest.type, handleGoogleLogin);
  yield takeLatest(logoutRequest.type, handleLogout);
}
